import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicQueueStatus } from "../../services/queue.service";
import type { PublicQueueStatus } from "../../services/queue.service";

const POLL_INTERVAL_MS = 25_000;

const STATE_COPY: Record<string, { title: string; body: string; tone: string }> = {
  waiting: { title: "You're in the queue", body: "We'll see you soon.", tone: "text-cyan-600" },
  in_progress: { title: "You're being seen now", body: "Thanks for your patience.", tone: "text-amber-600" },
  deferred: { title: "You're still in the queue", body: "Your turn has been slightly delayed.", tone: "text-purple-600" },
  seen: { title: "Visit complete", body: "Thank you for visiting us today.", tone: "text-green-600" },
  left: { title: "You're no longer in the queue", body: "Please visit the front desk if this seems wrong.", tone: "text-slate-500" },
  rejected: { title: "Unable to see you today", body: "Please call the clinic or visit again to reschedule.", tone: "text-red-500" },
};

const QueueStatusPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<PublicQueueStatus | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!token) return;

    const fetchStatus = async () => {
      try {
        const s = await getPublicQueueStatus(token);
        setStatus(s);
        setNotFound(false);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [token]);

  const copy = status ? STATE_COPY[status.state] ?? STATE_COPY.waiting : null;
  const isActive = status && ["waiting", "in_progress", "deferred"].includes(status.state);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f8fc] px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-slate-100 p-6 text-center space-y-4">
        {loading ? (
          <p className="text-sm text-slate-400">Loading your queue status…</p>
        ) : notFound ? (
          <>
            <h1 className="text-lg font-semibold text-slate-800">Link expired or invalid</h1>
            <p className="text-sm text-slate-500">
              This queue link is no longer active. Please contact the clinic for help.
            </p>
          </>
        ) : status && copy ? (
          <>
            <h1 className={`text-lg font-semibold ${copy.tone}`}>{copy.title}</h1>
            {isActive && (
              <div className="py-4">
                <div className="text-4xl font-bold text-slate-800">#{status.position}</div>
                <div className="text-xs text-slate-400 mt-1">Your position in the queue</div>
                {status.eta_minutes != null && (
                  <div className="mt-3 text-sm text-slate-600">
                    Estimated wait: <span className="font-semibold">~{status.eta_minutes} min</span>
                  </div>
                )}
              </div>
            )}
            <p className="text-sm text-slate-500">{copy.body}</p>
            <p className="text-[11px] text-slate-300 pt-2">Updates automatically — no need to refresh.</p>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default QueueStatusPage;
